import { useState } from "react";
import clsx from "clsx";
import { api } from "@/lib/apiClient";
import { countNightsLocal } from "@/lib/bookingDates";
import { formatGrossFromNet } from "@/lib/money";
import {
  canDeleteInvoiceLine,
  canEditDraftInvoiceLines,
} from "@/lib/invoiceLineEdit";
import {
  defaultLinePeriod,
  formatInvoiceLinePeriod,
  isPerNightInvoiceLine,
  validateLinePeriod,
} from "@/lib/invoiceLinePeriod";
import { Button } from "@/components/ui/Button";
import type { Booking, Invoice, InvoiceLine } from "@/types/camping";

export interface InvoiceLinesEditorProps {
  invoice: Invoice;
  booking?: Booking | null;
  onChanged?: () => void;
  disabled?: boolean;
  /** Überschrift ausblenden, wenn der umgebende Bereich sie schon zeigt. */
  hideHeading?: boolean;
}

function formatQuantity(quantity: string): string {
  const n = Number(quantity);
  if (!Number.isFinite(n)) return quantity;
  return new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(n);
}

function lineAmountCaption(
  line: InvoiceLine,
  invoice: Invoice,
  perNight: boolean,
  periodLabel: string | null,
): string {
  const unit = formatGrossFromNet(line.unit_price, invoice.tax_rate_percent, invoice.currency);
  const qty = formatQuantity(line.quantity);
  if (perNight && line.period_start && line.period_end) {
    const nights = countNightsLocal(line.period_start, line.period_end);
    const netUnit = Number(line.unit_price);
    const quantity = Number(line.quantity);
    const actual = Number(line.line_total);
    const perNightTotal = quantity * nights * netUnit;
    const periodTotal = quantity * netUnit;
    const nightLabel = nights === 1 ? "Nacht" : "Nächte";
    if (Number.isFinite(actual) && Math.abs(actual - perNightTotal) <= 0.05) {
      const calc = `${qty} × ${nights} ${nightLabel} × ${unit}`;
      return periodLabel ? `${periodLabel} · ${calc}` : calc;
    }
    if (Number.isFinite(actual) && Math.abs(actual - periodTotal) <= 0.05) {
      const calc = `${qty} × ${unit}`;
      return periodLabel ? `${periodLabel} · ${calc}` : calc;
    }
    return periodLabel ?? "";
  }
  const calc = `${qty} × ${unit}`;
  return periodLabel ? `${periodLabel} · ${calc}` : calc;
}

export function InvoiceLinesEditor({
  invoice,
  booking = null,
  onChanged,
  disabled = false,
  hideHeading = false,
}: InvoiceLinesEditorProps) {
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [quantityDraft, setQuantityDraft] = useState("");
  const [periodStartDraft, setPeriodStartDraft] = useState("");
  const [periodEndDraft, setPeriodEndDraft] = useState("");
  const [lineAction, setLineAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const editable = canEditDraftInvoiceLines(invoice) && !disabled;

  function startEditing(line: InvoiceLine) {
    setEditingLineId(line.id);
    setQuantityDraft(String(line.quantity));
    const defaults = defaultLinePeriod(line, booking);
    setPeriodStartDraft(defaults?.periodStart ?? "");
    setPeriodEndDraft(defaults?.periodEnd ?? "");
    setError(null);
  }

  async function saveLine(line: InvoiceLine) {
    const perNight = isPerNightInvoiceLine(line);
    const originalPeriod = defaultLinePeriod(line, booking);
    const periodChanged =
      perNight &&
      originalPeriod != null &&
      (periodStartDraft !== originalPeriod.periodStart ||
        periodEndDraft !== originalPeriod.periodEnd);

    const payload: {
      quantity?: string;
      period_start?: string;
      period_end?: string;
    } = {};

    if (periodChanged) {
      const periodError = validateLinePeriod(periodStartDraft, periodEndDraft, booking);
      if (periodError) {
        setError(periodError);
        return;
      }
      payload.period_start = periodStartDraft;
      payload.period_end = periodEndDraft;
    }

    const qty = parseFloat(quantityDraft.replace(",", "."));
    if (!Number.isFinite(qty) || qty <= 0) {
      setError("Bitte eine gültige Menge eingeben.");
      return;
    }
    const quantityChanged = qty !== Number(line.quantity);
    if (quantityChanged || !perNight) {
      payload.quantity = String(qty);
    } else if (periodChanged) {
      payload.quantity = String(qty);
    }

    if (!payload.quantity && !payload.period_start) {
      setError("Keine Änderung erkannt.");
      return;
    }

    setLineAction(`update-${line.id}`);
    setError(null);
    try {
      await api.patch<Invoice>(`/api/v1/invoices/${invoice.id}/lines/${line.id}`, payload);
      setEditingLineId(null);
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Position konnte nicht gespeichert werden.");
    } finally {
      setLineAction(null);
    }
  }

  async function removeLine(line: InvoiceLine) {
    if (!window.confirm(`Position „${line.description}" entfernen?`)) {
      return;
    }
    setLineAction(`delete-${line.id}`);
    setError(null);
    try {
      await api.del<Invoice>(`/api/v1/invoices/${invoice.id}/lines/${line.id}`);
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Position konnte nicht entfernt werden.");
    } finally {
      setLineAction(null);
    }
  }

  if (invoice.lines.length === 0) {
    return null;
  }

  return (
    <div>
      {hideHeading ? null : (
        <h4 className="text-sm font-semibold text-label">Positionen</h4>
      )}
      {error ? <p className="mt-2 text-sm text-status-red">{error}</p> : null}
      <ul className={clsx("text-sm", hideHeading ? "mt-1" : "mt-2")}>
        {invoice.lines.map((line) => {
          const isEditing = editingLineId === line.id;
          const busy = lineAction === `update-${line.id}` || lineAction === `delete-${line.id}`;
          const perNight = isPerNightInvoiceLine(line);
          const periodLabel = formatInvoiceLinePeriod(line);
          const lineTotal = formatGrossFromNet(
            line.line_total,
            invoice.tax_rate_percent,
            invoice.currency,
          );
          return (
            <li
              key={line.id}
              className={clsx(
                "flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between",
                isEditing
                  ? "my-1 rounded-lg border border-separator bg-fill-tertiary px-3"
                  : "border-b border-separator last:border-b-0",
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3">
                  <p className="font-medium text-label">{line.description}</p>
                  {isEditing ? null : (
                    <p className="shrink-0 tabular-nums font-semibold text-label">{lineTotal}</p>
                  )}
                </div>
                {isEditing ? (
                  <div className="mt-2 space-y-2">
                    {perNight ? (
                      <div className="grid gap-2 sm:grid-cols-2">
                        <label className="flex flex-col gap-1 text-xs text-label-secondary">
                          Leistung von
                          <input
                            type="date"
                            className="eh-input"
                            value={periodStartDraft}
                            min={booking?.arrival_date}
                            max={booking?.departure_date}
                            onChange={(e) => setPeriodStartDraft(e.target.value)}
                            disabled={busy}
                          />
                        </label>
                        <label className="flex flex-col gap-1 text-xs text-label-secondary">
                          Leistung bis (exkl.)
                          <input
                            type="date"
                            className="eh-input"
                            value={periodEndDraft}
                            min={booking?.arrival_date}
                            max={booking?.departure_date}
                            onChange={(e) => setPeriodEndDraft(e.target.value)}
                            disabled={busy}
                          />
                        </label>
                      </div>
                    ) : null}
                    <label className="flex flex-col gap-1 text-xs text-label-secondary">
                      Menge
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        className="eh-input max-w-[8rem]"
                        value={quantityDraft}
                        onChange={(e) => setQuantityDraft(e.target.value)}
                        disabled={busy}
                      />
                    </label>
                    {perNight ? (
                      <p className="text-xs text-label-tertiary">
                        Menge = Personen bzw. Stück. Der Zeitraum bestimmt die Nächte; die Summe
                        wird aus Menge × Nächte × Einzelpreis berechnet. Für abweichende
                        Anreisedaten können Sie die Menge reduzieren und eine weitere Position
                        hinzufügen.
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <p className="mt-0.5 text-xs text-label-secondary">
                    {lineAmountCaption(line, invoice, perNight, periodLabel)}
                  </p>
                )}
              </div>
              {editable ? (
                <div className="flex shrink-0 flex-wrap gap-2">
                  {isEditing ? (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        loading={lineAction === `update-${line.id}`}
                        disabled={!!lineAction}
                        onClick={() => void saveLine(line)}
                      >
                        Speichern
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={!!lineAction}
                        onClick={() => {
                          setEditingLineId(null);
                          setError(null);
                        }}
                      >
                        Abbrechen
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={!!lineAction}
                        onClick={() => startEditing(line)}
                      >
                        {perNight ? "Bearbeiten" : "Menge ändern"}
                      </Button>
                      {canDeleteInvoiceLine(invoice, invoice.lines) ? (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          loading={lineAction === `delete-${line.id}`}
                          disabled={!!lineAction}
                          onClick={() => void removeLine(line)}
                        >
                          Entfernen
                        </Button>
                      ) : null}
                    </>
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-label-tertiary">Beträge inkl. MwSt.</p>
    </div>
  );
}
